import { DndContext } from '@dnd-kit/core';
import { DragEndEvent } from '@dnd-kit/core/dist/types';
import { arrayMove, SortableContext, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import { compareByString, isPresent } from '@luminovo/commons';
import {
    colorSystem,
    DestructiveTertiaryIconButton,
    Divider,
    Flexbox,
    MenuItem,
    SearchInput,
    SecondaryButton,
    TertiaryButton,
    TertiaryIconButton,
    Text,
    Tooltip,
} from '@luminovo/design-system';
import { Badge, ListItem, Menu } from '@mui/material';
import { useSelector } from '@tanstack/react-store';
import { ArrowUpDownIcon, GripVerticalIcon, PlusIcon, XIcon } from 'lucide-react';
import React, { type JSX } from 'react';

import { ColumnSort, RowData, Table } from '../tanstackTypes';

type SortMenuState =
    | {
          type: 'None';
          anchorEl: undefined;
      }
    | {
          type: 'AddSort';
          anchorEl: Element;
      }
    | {
          type: 'SortManager';
          anchorEl: Element;
      };

export function SortMenu<TData extends RowData>({ table }: { table: Table<TData> }): JSX.Element {
    const [menuState, setMenuState] = React.useState<SortMenuState>({ type: 'None', anchorEl: undefined });

    const sortCount = table.getAllLeafColumns().filter((col) => col.getIsSorted()).length;
    const hasSortingEnabled = table.getAllLeafColumns().some((col) => col.getCanSort());

    const handleOnClick = React.useCallback(
        (e: React.MouseEvent<HTMLButtonElement>) => {
            if (menuState.type !== 'None') {
                return;
            }
            if (sortCount === 0) {
                setMenuState({ type: 'AddSort', anchorEl: e.currentTarget });
            } else {
                setMenuState({ type: 'SortManager', anchorEl: e.currentTarget });
            }
        },
        [menuState, sortCount, setMenuState],
    );

    const handleOpenAddSortMenu = () => {
        if (menuState.type === 'None') {
            return;
        }
        setMenuState({ type: 'AddSort', anchorEl: menuState.anchorEl });
    };

    const handleClose = () => {
        setMenuState({ type: 'None', anchorEl: undefined });
    };

    if (!hasSortingEnabled) {
        return <></>;
    }

    return (
        <>
            <Badge badgeContent={sortCount} color={'primary'}>
                <SecondaryButton size={'medium'} onClick={handleOnClick} startIcon={<ArrowUpDownIcon />}>
                    <Trans>Sort</Trans>
                </SecondaryButton>
            </Badge>

            <Menu
                anchorEl={menuState.anchorEl}
                open={menuState.type === 'SortManager'}
                onClose={handleClose}
                variant="menu"
                keepMounted
            >
                <SortManager table={table} onClose={handleClose} />
                <Divider />
                <AddSortMenuItem
                    table={table}
                    menuState={menuState}
                    onClick={handleOpenAddSortMenu}
                    onClose={handleClose}
                />
                <DeleteSortMenuItem table={table} onClose={handleClose} />
            </Menu>
        </>
    );
}

function SortManager<TData extends RowData>({
    table,
    onClose,
}: {
    table: Table<TData>;
    onClose: () => void;
}): JSX.Element {
    const sorting = useSelector(table.store, (state) => state.sorting);

    // Only render entries whose column actually exists on the current table. Sorts persisted from
    // a sibling tab with a different column set stay in state but are invisible here — they
    // re-appear on tabs that do have the column.
    const visibleSorting = sorting.filter((s) => isPresent(table.getColumn(s.id)));

    const onDragEnd = ({ over, active }: DragEndEvent) => {
        if (!isPresent(over)) {
            return;
        }

        const overIndex = sorting.findIndex((sort) => sort.id === over.id);
        const activeIndex = sorting.findIndex((sort) => sort.id === active.id);

        table.setSorting(arrayMove(sorting, activeIndex, overIndex));
    };

    return (
        <ListItem style={{ backgroundColor: colorSystem.neutral.white, padding: '8px' }}>
            <DndContext onDragEnd={onDragEnd}>
                <SortableContext items={visibleSorting}>
                    <Flexbox
                        gap={8}
                        sx={{
                            flexDirection: 'column',
                        }}
                    >
                        {visibleSorting.map((columnSort) => (
                            <SortManagerItem
                                key={columnSort.id}
                                columnSort={columnSort}
                                table={table}
                                handleClose={onClose}
                            />
                        ))}
                    </Flexbox>
                </SortableContext>
            </DndContext>
        </ListItem>
    );
}

function SortManagerItem<TData extends RowData>({
    columnSort,
    table,
    handleClose,
}: {
    columnSort: ColumnSort;
    table: Table<TData>;
    handleClose: () => void;
}) {
    const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id: columnSort.id });
    const column = table.getColumn(columnSort.id);
    const onlyHasOneSort = table.getAllLeafColumns().filter((col) => col.getIsSorted()).length === 1;

    if (!column) return null;

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
    };

    const handleSwitchSort = () => {
        if (column.getIsSorted() === 'asc') {
            column.toggleSorting(true, true);
        }
        if (column.getIsSorted() === 'desc') {
            column.toggleSorting(false, true);
        }
    };

    const handleRemoveSort = () => {
        column.clearSorting();
        if (onlyHasOneSort) {
            handleClose();
        }
    };

    return (
        <div ref={setNodeRef} style={style}>
            <Flexbox
                gap={12}
                sx={{
                    justifyContent: 'space-between',
                    alignItems: 'center',
                }}
            >
                <Flexbox
                    sx={{
                        alignItems: 'center',
                    }}
                >
                    <TertiaryIconButton
                        size="small"
                        aria-label={t`Drag to reorder`}
                        disableRipple
                        ref={setNodeRef}
                        style={{ cursor: 'grab', color: colorSystem.neutral[5] }}
                        {...listeners}
                        {...attributes}
                        disabled={onlyHasOneSort}
                    >
                        <GripVerticalIcon />
                    </TertiaryIconButton>

                    <Tooltip
                        title={
                            column.getIsSorted() === 'asc'
                                ? t`Switch sorting to descending`
                                : t`Switch sorting to ascending`
                        }
                        placement="top"
                    >
                        <TertiaryButton
                            size={'small'}
                            onClick={handleSwitchSort}
                            endIcon={
                                column.getIsSorted() === 'asc' ? (
                                    <Text variant={'body-small'} color={colorSystem.neutral[6]}>
                                        <Trans>(ascending)</Trans>
                                    </Text>
                                ) : (
                                    <Text variant={'body-small'} color={colorSystem.neutral[6]}>
                                        <Trans>(descending)</Trans>
                                    </Text>
                                )
                            }
                        >
                            <Flexbox
                                gap={24}
                                sx={{
                                    justifyContent: 'space-between',
                                }}
                            >
                                <Text
                                    variant={'body-small'}
                                    showEllipsis={true}
                                    style={{ width: '200px', textAlign: 'left' }}
                                >
                                    {(() => {
                                        const parentId = column.columnDef.meta?.parentColumnId;
                                        const parentCol = parentId ? table.getColumn(parentId) : undefined;
                                        const parentLbl = parentCol?.columnDef.meta?.label();
                                        const label = column.columnDef.meta?.label() ?? t`Unknown`;
                                        return parentLbl ? `${parentLbl} > ${label}` : label;
                                    })()}
                                </Text>
                            </Flexbox>
                        </TertiaryButton>
                    </Tooltip>
                </Flexbox>
                <Tooltip title={t`Remove sorting rule`} placement="top">
                    <DestructiveTertiaryIconButton
                        aria-label={t`Remove sorting rule`}
                        size={'small'}
                        onClick={handleRemoveSort}
                    >
                        <XIcon />
                    </DestructiveTertiaryIconButton>
                </Tooltip>
            </Flexbox>
        </div>
    );
}

function AddSortMenuItem<TData extends RowData>({
    table,
    menuState,
    onClick,
    onClose,
}: {
    table: Table<TData>;
    menuState: SortMenuState;
    onClick: () => void;
    onClose: () => void;
}): JSX.Element {
    const [query, setQuery] = React.useState<string>('');

    const allSortableColumns = table.getAllLeafColumns().filter((col) => col.getCanSort());

    const unsortedColumns = allSortableColumns.filter((col) => !col.getIsSorted());

    const additionalSortsByParent = new Map<string, typeof unsortedColumns>();
    for (const col of unsortedColumns) {
        const parentId = col.columnDef.meta?.parentColumnId;
        if (col.columnDef.meta?.isAdditionalFilter && parentId) {
            const existing = additionalSortsByParent.get(parentId) ?? [];
            existing.push(col);
            additionalSortsByParent.set(parentId, existing);
        }
    }

    const allTopLevelColumns = allSortableColumns
        .filter((col) => !col.columnDef.meta?.isAdditionalFilter)
        .sort((a, b) => compareByString(a.columnDef.meta?.label() ?? '', b.columnDef.meta?.label() ?? ''));

    const matchesQuery = (label: string) => label.toLocaleLowerCase().includes(query);

    const filteredEntries: Array<{ column: (typeof allSortableColumns)[number]; label: string }> = [];
    for (const col of allTopLevelColumns) {
        const parentLabel = col.columnDef.meta?.label() ?? t`Unknown`;
        if (!col.getIsSorted() && matchesQuery(parentLabel)) {
            filteredEntries.push({ column: col, label: parentLabel });
        }
        const children = additionalSortsByParent.get(col.id);
        if (children) {
            for (const child of children) {
                const childLabel = child.columnDef.meta?.label() ?? t`Unknown`;
                const compositeLabel = `${parentLabel} > ${childLabel}`;
                if (matchesQuery(parentLabel) || matchesQuery(childLabel)) {
                    filteredEntries.push({ column: child, label: compositeLabel });
                }
            }
        }
    }

    return (
        <>
            <MenuItem label={t`Add sort`} onClick={onClick} startIcon={<PlusIcon />} />

            <Menu
                anchorEl={menuState.anchorEl}
                open={menuState.type === 'AddSort'}
                onClose={onClose}
                variant="menu"
                keepMounted
            >
                <ListItem style={{ backgroundColor: colorSystem.neutral.white }} onKeyDown={(e) => e.stopPropagation()}>
                    <SearchInput
                        placeholder={t`Sort by...`}
                        value={query}
                        onChange={(value) => setQuery(value)}
                        onClear={() => setQuery('')}
                        style={{ backgroundColor: colorSystem.neutral.white }}
                    />
                </ListItem>

                {filteredEntries.map(({ column, label }) => (
                    <MenuItem
                        key={column.id}
                        onClick={() => {
                            column.toggleSorting(undefined, true);
                            onClose();
                        }}
                        label={label}
                        style={column.columnDef.meta?.isAdditionalFilter ? { paddingLeft: 32 } : undefined}
                    />
                ))}

                {filteredEntries.length === 0 && (
                    <ListItem style={{ backgroundColor: colorSystem.neutral.white }}>
                        <Text variant={'body-small'} color={colorSystem.neutral[6]}>
                            <Trans>No results</Trans>
                        </Text>
                    </ListItem>
                )}
            </Menu>
        </>
    );
}

function DeleteSortMenuItem<TData extends RowData>({
    table,
    onClose,
}: {
    table: Table<TData>;
    onClose: () => void;
}): JSX.Element {
    const hasSorted = table.getAllLeafColumns().some((col) => col.getIsSorted());

    const handleDeleteSorting = () => {
        table
            .getAllLeafColumns()
            .filter((col) => col.getCanSort())
            .forEach((col) => col.clearSorting());
        onClose();
    };

    return (
        <MenuItem
            label={t`Clear sort`}
            variant={'destructive'}
            startIcon={<XIcon />}
            onClick={handleDeleteSorting}
            disabled={!hasSorted}
        />
    );
}
